import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from './components/PageHeader';
import { apiFetch } from './utils/api';

const AUDIENCE_LABELS = {
  commercial: 'Commercial',
  domestic: 'Residential',
};

function MarketingMasterTemplates() {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState('commercial');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/api/marketing/master-templates');
      const data = await response.json();
      const rows = data.templates || [];
      setTemplates(rows);
      setSelectedKey(current => (rows.some(template => template.key === current) ? current : rows[0]?.key || current));
    } catch (err) {
      setError(err.message || 'Unable to load master templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const selected = templates.find(template => template.key === selectedKey) || templates[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Master Templates"
        subtitle="Approved newsletter masters"
        backTo="/marketing-hub"
        backLabel="Marketing Hub"
        sticky
      />

      <main className="max-w-7xl mx-auto px-6 py-6">
        {error && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded border border-gray-200 bg-white p-8 text-center text-gray-500">
            Loading master templates...
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-5">
            <aside className="space-y-3">
              {templates.map(template => (
                <button
                  key={template.key}
                  type="button"
                  onClick={() => setSelectedKey(template.key)}
                  className={`w-full rounded border bg-white p-4 text-left transition ${
                    selected?.key === template.key
                      ? 'border-[#172378] ring-2 ring-[#172378]/15'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">{template.name}</h2>
                      <p className="mt-1 text-sm text-gray-500">{AUDIENCE_LABELS[template.category] || template.category}</p>
                    </div>
                    <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                      {template.version}
                    </span>
                  </div>
                </button>
              ))}
            </aside>

            {selected && (
              <section className="space-y-4">
                <div className="rounded border border-gray-200 bg-white">
                  <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{selected.name}</h2>
                      <p className="text-sm text-gray-500">{AUDIENCE_LABELS[selected.category] || selected.category}</p>
                    </div>
                    <button
                      type="button"
                      onClick={loadTemplates}
                      className="self-start rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 md:self-auto"
                    >
                      Refresh
                    </button>
                  </div>
                  <div className="bg-gray-100 p-4">
                    <iframe
                      title={selected.name}
                      srcDoc={selected.preview_html}
                      className="h-[760px] w-full rounded border border-gray-300 bg-white"
                      sandbox="allow-same-origin"
                    />
                  </div>
                </div>

                <div className="rounded border border-gray-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-normal text-gray-600">Content Slots</h3>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {(selected.content_variables || selected.required_variables || []).map(slot => (
                      <div key={slot.variable} className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {slot.variable}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default MarketingMasterTemplates;
